import { UntitledIcon } from '@faceup/icons'
import { usAttachment02 } from '@faceup/icons/usAttachment02'
import { Button } from '@faceup/ui-base'
import { type ReactNode, useContext, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { UiContext } from '../UiProvider/UiProvider'

type ExtendedData = { [key: string]: unknown }

type AttachmentType<D extends ExtendedData = ExtendedData> = {
  id: string
  name: string
  url: string
  mimetype: string
} & D

type Variant = 'default' | 'with-bg'

const variants: Record<Variant, string> = {
  default: '',
  'with-bg': 'max-w-full py-2 px-3 rounded-lg bg-colorFillContent',
}

type Config<D extends ExtendedData> = {
  onDownload: ((payload: AttachmentType<D>) => Promise<boolean>) | null
  renderFileName?: (children: ReactNode, attachment: AttachmentType<D>) => ReactNode
  isDownloadDisabled?: boolean
  variant?: Variant
}

type AttachmentProps<D extends ExtendedData> = {
  attachment: AttachmentType<D>
  config: Config<D>
}

const AttachmentFileName = <D extends ExtendedData>({ attachment, config }: AttachmentProps<D>) => {
  const fileName = (
    <span className='block max-w-full truncate font-normal text-sm'>{attachment.name}</span>
  )
  return config.renderFileName ? config.renderFileName(fileName, attachment) : fileName
}

const Attachment = <D extends ExtendedData>({ attachment, config }: AttachmentProps<D>) => {
  const [loading, setLoading] = useState(false)
  const { attachmentList } = useContext(UiContext)
  const variant = config.variant ?? 'default'

  return (
    <div className={twMerge('flex min-w-0 max-w-full items-center gap-2', variants[variant])}>
      <UntitledIcon
        icon={usAttachment02}
        size={18}
        className='shrink-0 text-colorTextDescription'
      />
      {config.isDownloadDisabled ? (
        <span className='min-w-0 max-w-full overflow-hidden'>
          <AttachmentFileName attachment={attachment} config={config} />
        </span>
      ) : (
        <Button
          type={config.onDownload ? 'link' : 'text'}
          size='small'
          className='min-w-0 max-w-full overflow-hidden px-0 [&>span]:min-w-0 [&>span]:max-w-full [&>span]:overflow-hidden'
          loading={loading}
          onClick={
            config.onDownload
              ? async () => {
                  setLoading(true)
                  await config.onDownload?.(attachment)
                  setLoading(false)
                }
              : undefined
          }
          label={config.onDownload ? attachment.name : undefined}
          aria-label={
            config.onDownload
              ? `${attachmentList?.download ?? 'Download'} ${attachment.name}`
              : undefined
          }
        >
          <AttachmentFileName attachment={attachment} config={config} />
        </Button>
      )}
    </div>
  )
}

type AttachmentListProps<D extends ExtendedData = ExtendedData> = {
  attachments: AttachmentType<D>[]
  config: Config<D>
}

export const AttachmentList = <D extends ExtendedData = ExtendedData>(
  props: AttachmentListProps<D>
) => {
  if (!props.attachments.length) {
    return null
  }

  return (
    <div className='mt-2 flex flex-col gap-2'>
      {props.attachments.map(attachment => (
        <Attachment key={attachment.url} attachment={attachment} config={props.config} />
      ))}
    </div>
  )
}
